import type {
  ChiefComplaintRow,
  ChiefComplaintSet,
  ComplaintSuggestions,
} from './chiefComplaints.types'

type Row = Record<string, unknown>

// One complaint, and the search for one, is at most this long.
export const COMPLAINT_MAX_LENGTH = 255
const NUMERIC_ID = /^\d+$/
const NOT_SYNCED = /not synced|unknown appointment/i

export const EMPTY_COMPLAINT_SET: ChiefComplaintSet = {
  complaints: [],
  frozen: false,
  frozenReason: null,
  hasUnreadableRows: false,
}

const asRow = (value: unknown): Row | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Row) : null

const isText = (value: unknown): value is string => typeof value === 'string' && !!value.trim()

// The one comparison every duplicate check uses; inner spaces are kept, as OpenEMR does.
export const normalizeComplaint = (text: string): string => text.trim().toLowerCase()

export const toComplaintQuery = (text: string): string => text.trim().slice(0, COMPLAINT_MAX_LENGTH)

export const toComplaintText = (text: string): string =>
  text.trim().slice(0, COMPLAINT_MAX_LENGTH).trim()

// A positive integer, or one sent as a digit string ("12"), as PDO can.
const readId = (value: unknown): number | null => {
  const id = typeof value === 'string' && NUMERIC_ID.test(value) ? Number(value) : value
  return typeof id === 'number' && Number.isSafeInteger(id) && id > 0 ? id : null
}

const readRow = (value: unknown): ChiefComplaintRow | null => {
  const row = asRow(value)
  if (!row) return null
  const id = readId(row.id)
  if (id === null || !isText(row.complaint)) return null
  const sequence = typeof row.sequence === 'number' ? row.sequence : Number.MAX_SAFE_INTEGER
  return { id, sequence, complaint: row.complaint }
}

export const transformChiefComplaintSet = (response: unknown): ChiefComplaintSet => {
  const data = asRow(asRow(response)?.data)
  if (!data) return EMPTY_COMPLAINT_SET

  const values: unknown[] = Array.isArray(data.chief_complaints) ? data.chief_complaints : []
  const read = values.map(readRow)
  const rows = read.filter((row): row is ChiefComplaintRow => row !== null)

  return {
    complaints: [...rows].sort((left, right) => left.sequence - right.sequence),
    frozen: data.frozen === true,
    frozenReason: isText(data.frozen_reason) ? data.frozen_reason : null,
    hasUnreadableRows: rows.length !== read.length,
  }
}

const readTexts = (value: unknown): string[] => (Array.isArray(value) ? value.filter(isText) : [])

export const transformComplaintSuggestions = (response: unknown): ComplaintSuggestions => {
  const data = asRow(asRow(response)?.data)
  return { patient: readTexts(data?.patient), practice: readTexts(data?.practice) }
}

export const getErrorStatus = (error: unknown): number | null => {
  const status = asRow(error)?.status
  return typeof status === 'number' ? status : null
}

export const isNotSyncedMessage = (message: string | null): boolean =>
  !!message && NOT_SYNCED.test(message)
